<script setup lang="ts">
import { computed, reactive } from 'vue'
import type { ScenarioChatForm_Message_BasicSelections } from '../../types'
import MessageSelection from './MessageSelection.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import IconPlus from '@/components/icons/IconPlus.vue'
import { useInternalScenarioChatFormStore } from '../../form/form'
import { nanoid } from 'nanoid'

const props = defineProps<{
  selectedMessageClientOnlyId: string
  basicSelections: ScenarioChatForm_Message_BasicSelections
  isDeletable: boolean
}>()

const formStore = reactive(useInternalScenarioChatFormStore())

const isSelectionDeletable = computed(() => {
  return props.basicSelections.basicSelections.length > 2
})

const handleUpdateSelectionLabel = (clientOnlyId: string, label: string) => {
  formStore.updateMessage(props.selectedMessageClientOnlyId, draft => {
    if (draft.selections.type !== 'BasicSelections') {
      throw new Error('prevMessage.type is not BasicSelections')
    }

    draft.selections.basicSelections.map(selection =>
      selection.clientOnlyId === clientOnlyId
        ? { ...selection, label }
        : selection
    )
  })
}

const handleClickAddSelectionButton = (event: MouseEvent) => {
  event.preventDefault()

  formStore.updateMessage(props.selectedMessageClientOnlyId, draft => {
    if (draft.selections.type !== 'BasicSelections') {
      throw new Error('draft.selections.type is not BasicSelections')
    }

    draft.selections.basicSelections.push({
      guid: undefined,
      clientOnlyId: nanoid(),
      label: '',
      actions: [],
      nextScenarioChatMessageClientOnlyId: undefined,
    })
  })
}

const handleDeleteSelection = (index: number) => {
  formStore.updateMessage(props.selectedMessageClientOnlyId, draft => {
    if (draft.selections.type !== 'BasicSelections') {
      throw new Error('draft.selections.type is not BasicSelections')
    }

    draft.selections.basicSelections.splice(index, 1)
  })
}

const handleAddNextMessage = (selectionGuid: string) => {
  formStore.addNextMessage({
    messageClientOnlyId: props.selectedMessageClientOnlyId,
    selectionClientOnlyId: selectionGuid,
  })
}
</script>

<template>
  <div>
    <div>
      <MessageSelection
        v-for="(selection, i) of props.basicSelections.basicSelections"
        :key="selection.clientOnlyId"
        :class="$style.selection"
        :index="i"
        :selectedMessageClientOnlyId="props.selectedMessageClientOnlyId"
        :selection="selection"
        :isDeletable="isSelectionDeletable"
        @updateLabel="
          handleUpdateSelectionLabel(selection.clientOnlyId, $event)
        "
        @addNextMessage="handleAddNextMessage(selection.clientOnlyId)"
        @deleteSelection="handleDeleteSelection(i)"
      />
    </div>
    <div :class="$style.selectionFooter">
      <FlatButton
        variant="base"
        size="medium"
        @click="handleClickAddSelectionButton"
      >
        <template #icon><IconPlus /></template>
        選択肢を追加する
      </FlatButton>
    </div>
  </div>
</template>

<style lang="scss" module>
.selection {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 32px;
  margin-bottom: 32px;
}
</style>
